/**
 * AuditPage.tsx — Audit & Discrepanze surface orchestrator. Resolves which
 * project to show (route param, else most-recent), fetches its live audit data
 * (categories / line items / uncategorized) from the tender-api, and composes
 * Header + ActionPanel + UnifiedTable. Categorization is a real mutation; the
 * discrepancy status/comments are handled inside DiscrepancyContent.
 */
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { usePublishActiveProject } from "../../lib/activeProject";
import { apiErrorMessage } from "../../lib/api";
import { preparePath, projectHasAnalysis } from "../../lib/nav";
import {
  categoryReconciliationOf,
  extractionLossOf,
  itemsAccountOf,
  priceBookOverlapOf,
  reconciliationOf,
  useAuditCategories,
  useAuditLineItems,
  useBulkCategorizeLineItems,
  useCategorizeLineItem,
  useCheckSummary,
  useLatestAnalysisJob,
  useLineItemPages,
  useProject,
  useProjects,
  useTopLineItems,
} from "../../lib/tenderApi";
import { ActionPanel } from "./ActionPanel";
import { CategoryReconciliationBanner } from "./CategoryReconciliationBanner";
import { CheckSummaryPanel } from "./CheckSummaryPanel";
import { Header } from "./Header";
import { ManageCategoriesPanel } from "./ManageCategoriesPanel";
import { ExtractionLossBanner } from "./ExtractionLossBanner";
import { type ItemPagesView } from "./ItemBillPage";
import { PriceBookOverlapBanner } from "./PriceBookOverlapBanner";
import { ReconciliationBanner } from "./ReconciliationBanner";
import { UnifiedTable } from "./UnifiedTable";

function SectionHeader({ title, sub }: { title: string; sub: string }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
      }}
    >
      <div>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, letterSpacing: "-0.005em" }}>
          {title}
        </h2>
        <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

export function AuditPage(): JSX.Element {
  // Resolve which project's audit to show: the route param if present (deep
  // link from Ingest), otherwise the most-recently-updated project.
  const { projectId } = useParams<{ projectId?: string }>();
  const nav = useNavigate();
  const projectsQuery = useProjects();
  const explicitQuery = useProject(projectId ?? null);
  const project = projectId ? explicitQuery.data : projectsQuery.data?.[0];
  const projectLoading = projectId ? explicitQuery.isLoading : projectsQuery.isLoading;
  const projectError = projectId
    ? explicitQuery.isError
      ? apiErrorMessage(explicitQuery.error)
      : null
    : projectsQuery.isError
      ? apiErrorMessage(projectsQuery.error)
      : null;
  const pid = project?.id ?? null;

  // Tell the shell which project is on screen, so the Assistente CME rail can
  // answer about it. This page is the only place that knows: the route often
  // carries no id and we fall back to the most-recent project above.
  usePublishActiveProject(pid, project?.name ?? null);

  // The analysis lifecycle for this project: rediscovers an in-flight run
  // after reload/reconnect (it polls while active) and drives the table's
  // empty-state. `P-63`: the never-analyzed and failed states redirect away
  // before this renders, so what it drives now is progress and the
  // completed-but-empty case.
  const latestJob = useLatestAnalysisJob(pid).data ?? null;
  const topQuery = useTopLineItems(pid);
  /* `P-63` — this page no longer starts an analysis, so everything that used to
     stand behind that button is gone with it: `useStartAnalysis`, the
     `analysis:run` capability read, and the `missing_roles` 422 decoding. The
     capability check is not lost, it is single: the preparation screen holds the
     one Avvia in the product and already refuses it with a reason. Keeping a
     second copy here would be the two-matchers shape that
     `feedback_one_predicate_not_two_callers` records — and the copy that drifts
     is always the one nobody can reach any more. */

  // Live audit data for the resolved project.
  const categoriesQuery = useAuditCategories(pid);
  const lineItemsQuery = useAuditLineItems(pid);
  const uncatQuery = useAuditLineItems(pid, { uncategorized: true });
  const checkSummary = useCheckSummary(pid);
  // G3a — the bill page of every item, asked once for the page and handed to every table.
  const pagesQuery = useLineItemPages(pid);
  const itemPages: ItemPagesView = pagesQuery.isError
    ? { state: "failed" }
    : pagesQuery.data
      ? { state: "ready", pages: pagesQuery.data }
      : { state: "loading" };
  const categorize = useCategorizeLineItem(pid ?? "");
  const bulkCategorize = useBulkCategorizeLineItems(pid ?? "");

  const categories = categoriesQuery.data ?? [];
  const lineItems = lineItemsQuery.data ?? [];
  const uncat = uncatQuery.data ?? [];
  const auditLoading = pid != null && (lineItemsQuery.isLoading || categoriesQuery.isLoading);
  const auditError = lineItemsQuery.isError
    ? apiErrorMessage(lineItemsQuery.error)
    : categoriesQuery.isError
      ? apiErrorMessage(categoriesQuery.error)
      : null;
  const busy = categorize.isPending || bulkCategorize.isPending;

  /**
   * Does this project have an analysis the reconciliation banners should talk
   * about?
   *
   * Keyed on `lineItems.length > 0` alone, the answer was NO for a completed run
   * that extracted **zero rows** — the single case where the verdict says the
   * most, because 100% of the money is missing. Both banners open with
   * `if (!hasAnalysis) return null`, so that run showed no verdict here at all.
   * It did not matter while the intake card carried the verdict too; `P-32`
   * moved the whole signal onto this page, and its defence is "one click away",
   * so a case where the click leads to nothing is a hole `P-32` opened.
   *
   * Deliberately an EXTENSION, never a restriction: the row-count leg is
   * untouched, so nothing that rendered before stops rendering. The second leg
   * requires a verdict to actually EXIST — `reconciliationCopy(null, …)` has a
   * "non verificati" branch, and admitting a null verdict here would have put a
   * banner on projects that were never analysed. And it requires the queries to
   * have settled, because `lineItems` is equally empty while loading and on
   * error, which is how a naive version of this fix flashes a verdict on every
   * page load.
   */
  const auditSettled = !auditLoading && auditError === null;
  const recVerdict = latestJob ? reconciliationOf(latestJob) : null;
  const catVerdict = latestJob ? categoryReconciliationOf(latestJob) : null;
  const hasRecAnalysis = lineItems.length > 0 || (auditSettled && recVerdict !== null);
  const hasCatAnalysis = lineItems.length > 0 || (auditSettled && catVerdict !== null);
  // `P-47` — which price book won when two disagreed. Same `hasAnalysis`
  // derivation as its two siblings, so an un-analysed project shows nothing
  // rather than a reassuring silence about a check that never ran.
  const overlap = latestJob ? priceBookOverlapOf(latestJob) : null;
  const hasOverlapAnalysis = lineItems.length > 0 || (auditSettled && overlap !== null);
  // `P-52` — same three-state derivation as its siblings, so an un-analysed
  // project shows nothing rather than a reassuring silence about a read that
  // never happened.
  const extractionLoss = latestJob ? extractionLossOf(latestJob) : null;
  const hasExtractionLossAnalysis =
    lineItems.length > 0 || (auditSettled && extractionLoss !== null);
  // G3a — the run's items account (merges, drops, found vs saved); null for an older run.
  const itemsAccount = latestJob ? itemsAccountOf(latestJob) : null;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [managing, setManaging] = useState(false);

  function toggleSel(id: string): void {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function selectAll(checked: boolean): void {
    setSelected(checked ? new Set(uncat.map((t) => t.id)) : new Set());
  }

  function onAssign(itemId: string, categoryId: string): void {
    categorize.mutate({ lineItemId: itemId, categoryId });
    setSelected((s) => {
      const n = new Set(s);
      n.delete(itemId);
      return n;
    });
  }

  function onBulkAssign(categoryId: string): void {
    if (selected.size === 0) return;
    bulkCategorize.mutate(
      { ids: [...selected], categoryId },
      { onSuccess: () => setSelected(new Set()) },
    );
  }

  function toggleCat(id: string): void {
    setExpandedCats((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  /**
   * `P-63` — a project with no analysis has no audit, and must not offer to buy one.
   *
   * Omar, 2026-09-02: *"There should no longer be a pre-analysis Audit page from
   * which the user can press Avvia… I want that pre-analysis Audit state to
   * disappear entirely from the user flow."*
   *
   * Enforced HERE, at the destination, and not on each of the five links that
   * reach it. Those are routed too — otherwise the wrong page paints for a
   * frame — but a guard repeated per door is a guard the sixth door forgets,
   * and this page is also reachable by a bookmark, a reload and the browser's
   * Back button, which have no call site to fix. Same reasoning as
   * `feedback_a_guard_on_the_cheaper_button`: one transition, one authority.
   *
   * `replace` so Back does not bounce the user between the two screens.
   *
   * Cannot loop: the destination is `IngestPage`, which redirects nowhere. And
   * it waits for the project — redirecting while `project` is undefined would
   * fire on every first render, including for analysed projects.
   */
  if (project && !projectHasAnalysis(project.status)) {
    return <Navigate to={preparePath(project.id)} replace />;
  }

  if (projectError) {
    return (
      <div style={{ padding: "20px 28px 60px" }}>
        <div className="card" style={{ padding: 24, textAlign: "center" }}>
          <div style={{ color: "var(--c-err)", fontSize: 13, marginBottom: 10 }}>
            {projectError}
          </div>
          <Link to="/" className="btn btn-sm">
            Torna ai progetti
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <Header key={pid ?? "none"} project={project} loading={projectLoading} />

      {/* The totals check's verdict, immediately under the Header and above the
          table.

          An earlier version of this comment said the banner sits "above
          everything else… it qualifies every figure below it". That was false:
          `<Header>` renders first and carries the headline `Valore CME` stat, so
          the single most prominent number on the page is ABOVE its own caveat —
          and the test claiming to prove the ordering only compares the banner
          against the table, so it could never have caught that. Qualifying the
          Header stat itself is a separate change; the claim is corrected here
          rather than left standing.

          `jobStatus` is passed because a missing verdict has three different
          meanings, and only one of them is "this analysis predates the check". */}
      <ReconciliationBanner
        verdict={recVerdict}
        hasAnalysis={hasRecAnalysis}
        jobStatus={latestJob?.status ?? null}
      />

      {/* And the per-category verdict directly under it. Deliberately a SECOND
          banner rather than a line inside the first: the two answer different
          questions, and on TORINO the first says "verificati" in green while
          this one reports € 63.371,00 under the wrong sub-category. Folding it
          into the total's banner would let an `ok` total colour a wrong
          breakdown. */}
      <CategoryReconciliationBanner
        verdict={catVerdict}
        hasAnalysis={hasCatAnalysis}
        jobStatus={latestJob?.status ?? null}
      />

      {/* `P-47` — and WHICH price book priced the run, when more than one could
          have. A third banner rather than a line inside either of the two above,
          for the same reason they are two: this answers a question neither asks.
          The totals can reconcile to the cent and the breakdown can be right
          while every shared code was priced from the wrong edition — the money
          is internally consistent, and simply came from the other book. Neither
          reconciliation check can see that, because nothing left the document. */}
      <PriceBookOverlapBanner
        overlap={overlap}
        hasAnalysis={hasOverlapAnalysis}
        jobStatus={latestJob?.status ?? null}
      />

      {/* `P-52` — which blocks of the Elenco Prezzi the run failed to read.
          It belongs beside the reconciliation banners rather than on the
          completion screen because its consequence shows up HERE: a missing
          price-list description is what makes the regional comparison report
          «Descrizione difforme dal listino» about documents that in fact
          agree. A reader looking at those rows needs to know the extraction
          was incomplete, at the moment they are looking at them. */}
      <ExtractionLossBanner
        loss={extractionLoss}
        hasAnalysis={hasExtractionLossAnalysis}
        jobStatus={latestJob?.status ?? null}
      />

      {/* Goal 55A: what Step 1 checked — per state and reason over every item, open and
          resolved flags apart, and the analysis marker with the checks it rests on. */}
      <CheckSummaryPanel summary={checkSummary.data} itemsAccount={itemsAccount} />

      {/* Hide the whole "Azioni richieste" section once everything is
          categorized — an empty panel is just dead space. */}
      {uncat.length > 0 && (
        <>
          <SectionHeader title="Azioni richieste" sub={`${uncat.length} voci da categorizzare`} />
          <ActionPanel
            items={uncat}
            categories={categories}
            selected={selected}
            toggleSel={toggleSel}
            selectAll={selectAll}
            onAssign={onAssign}
            onBulkAssign={onBulkAssign}
            busy={busy}
            itemPages={itemPages}
          />
          <div style={{ height: 28 }} />
        </>
      )}

      <SectionHeader title="Categorie e voci" sub="Drill-down per categoria" />
      <UnifiedTable
        categories={categories}
        lineItems={lineItems}
        loading={auditLoading}
        error={auditError}
        projectId={pid ?? ""}
        openRow={openRow}
        openDiscrepancy={(item) => setOpenRow((cur) => (cur === item.id ? null : item.id))}
        itemPages={itemPages}
        expandedCats={expandedCats}
        toggleCat={toggleCat}
        analysisJob={latestJob}
        onOpenPreparation={pid ? () => nav(preparePath(pid)) : undefined}
        pareto={topQuery.data}
        paretoLoading={topQuery.isLoading}
        paretoError={topQuery.isError ? apiErrorMessage(topQuery.error) : null}
        onManageCategories={pid ? () => setManaging((m) => !m) : undefined}
        managingCategories={managing}
        /* Rendered INSIDE the table card, straight under the tab bar. Placing it
           after the table put it below hundreds of rows — present in the DOM,
           thousands of pixels below the button that opens it, so clicking the
           button looked like it did nothing. */
        managePanel={
          managing && pid ? (
            <ManageCategoriesPanel
              projectId={pid}
              categories={categories}
              lineItems={lineItems}
              onClose={() => setManaging(false)}
              itemPages={itemPages}
            />
          ) : null
        }
      />
    </div>
  );
}
